import { useEffect, useRef, useState } from 'react'

// Hover-to-open dropdown state machine, shared by DropDownMenu.tsx and any
// other hover-driven dropdown: opens on mouse-enter, closes on mouse-leave,
// and after a programmatic forceClose (e.g. selecting an item) blocks
// reopening until the mouse actually leaves and re-enters — otherwise the
// dropdown would immediately flicker back open under the still-hovering
// cursor.
export function useHoverDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const isClosingRef = useRef(false)
  const hasLeftAfterCloseRef = useRef(true)

  const open = () => {
    // Only open if the mouse has left after a previous forceClose
    if (isClosingRef.current && !hasLeftAfterCloseRef.current) {
      return
    }
    isClosingRef.current = false
    hasLeftAfterCloseRef.current = false
    setIsOpen(true)
  }

  const close = () => {
    if (isClosingRef.current) {
      hasLeftAfterCloseRef.current = true
    }
    setIsOpen(false)
  }

  // Closes without touching the closing/re-open bookkeeping — for callers
  // that need to hide the dropdown outright (e.g. it became locked while
  // open) rather than as part of the normal hover-leave flow.
  const closeImmediately = () => {
    setIsOpen(false)
  }

  const forceClose = () => {
    isClosingRef.current = true
    hasLeftAfterCloseRef.current = false
    setIsOpen(false)
  }

  // Reset the closing flag once the mouse actually moves after closing, so
  // hovering back in later opens the dropdown again.
  useEffect(() => {
    const handleMouseMove = () => {
      if (isClosingRef.current && !isOpen) {
        const timer = setTimeout(() => {
          isClosingRef.current = false
        }, 200)
        return () => clearTimeout(timer)
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isOpen])

  return { isOpen, isClosingRef, open, close, closeImmediately, forceClose }
}
